import React, { useState } from 'react';
import { showError, showSuccess } from '../../utils/toast';

interface CancelErrandModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (reason: string, otherReason?: string) => Promise<void>;
  errandTitle: string;
}

const CANCEL_REASONS = [
  { value: 'change_of_plans', label: 'Change of Plans', icon: '📅' },
  { value: 'found_alternative', label: 'Found Alternative Solution', icon: '🔍' },
  { value: 'driver_too_slow', label: 'Driver Response Too Slow', icon: '⏱️' },
  { value: 'price_too_high', label: 'Price Too High', icon: '💰' },
  { value: 'location_issue', label: 'Location Issue', icon: '📍' },
  { value: 'other', label: 'Other Reason', icon: '📝' }
];

const CancelErrandModal: React.FC<CancelErrandModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  errandTitle
}) => {
  const [selectedReason, setSelectedReason] = useState<string>('');
  const [otherReason, setOtherReason] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedReason) {
      showError('Please select a cancellation reason');
      return;
    }

    if (selectedReason === 'other' && !otherReason.trim()) {
      showError('Please provide a reason for cancellation');
      return;
    }

    setIsSubmitting(true);
    try {
      await onConfirm(selectedReason, selectedReason === 'other' ? otherReason : undefined);
      showSuccess('Errand cancelled successfully');
      handleClose();
    } catch (error) {
      console.error('Error cancelling errand:', error);
      showError('Failed to cancel errand. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setSelectedReason('');
    setOtherReason('');
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900">Cancel Errand</h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600"
            disabled={isSubmitting}
          >
            ✕
          </button>
        </div>

        <p className="text-gray-600 mb-6">
          Are you sure you want to cancel <strong>{errandTitle}</strong>? Please select a reason:
        </p>

        <form onSubmit={handleSubmit}>
          <div className="space-y-3 mb-6">
            {CANCEL_REASONS.map((reason) => (
              <label
                key={reason.value}
                className={`flex items-center space-x-3 p-3 border-2 rounded-lg cursor-pointer transition-colors ${
                  selectedReason === reason.value
                    ? 'border-green-500 bg-green-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <input
                  type="radio"
                  name="cancelReason"
                  value={reason.value}
                  checked={selectedReason === reason.value}
                  onChange={(e) => setSelectedReason(e.target.value)}
                  className="w-4 h-4 text-green-600 focus:ring-green-500"
                />
                <span className="text-xl">{reason.icon}</span>
                <span className="flex-1 text-sm font-medium text-gray-700">
                  {reason.label}
                </span>
              </label>
            ))}
          </div>

          {selectedReason === 'other' && (
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Please specify the reason:
              </label>
              <textarea
                value={otherReason}
                onChange={(e) => setOtherReason(e.target.value)}
                placeholder="Enter your reason for cancellation..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                rows={3}
                required
              />
            </div>
          )}

          <div className="flex space-x-3">
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              disabled={isSubmitting}
            >
              Keep Errand
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isSubmitting || !selectedReason}
            >
              {isSubmitting ? 'Cancelling...' : 'Cancel Errand'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CancelErrandModal;



